// src/encounter/intelligence-engine.ts
//
// The client for the Scrips Intelligence agent engine (scrips-clinician-demo `scripts/foundry/scripsServer.ts`), served on
// dev at the SAME origin as this app under /api/intelligence (an Ingress path on dev-practitioner-v2; no CORS, no new host).
// The engine reads the patient's record with the signed-in clinician's own token, serves only allow-listed patients, and
// writes nothing to the record: every answer is a draft that carries its rule and model-check verdicts and the record ids
// it cites. Design-neutral: any Intelligence rail design renders from `useIntelligenceRun`.
//
// A run streams NDJSON events: start, model-turn, tool-call, tool-result, draft, check-rule, check-fast, [check-consensus],
// done | error. Errors shown to the clinician are the engine's own fixed messages, never a body or a token.
import { useCallback, useEffect, useRef, useState } from 'react';
import { authStorage } from '@/auth/storage';

export const INTELLIGENCE_BASE = '/api/intelligence';

export interface IntelligenceRunRequest {
  agent: string;
  actionKind: string;
  question: string;
  patientId: string;
  /** Where the clinician asked from; the engine keeps it with the run. `chip`: a curated prompt, not free text. */
  origin: { tab: string; question: string; chip?: boolean };
}

/** One engine event. Only the fields this app reads are typed; the rest pass through untouched. */
export interface IntelligenceEvent {
  type: string;
  runId?: string;
  message?: string;
  verdict?: string;
  pass?: boolean;
  violations?: unknown[];
  problems?: unknown[];
  result?: IntelligenceResult;
  [key: string]: unknown;
}

export interface IntelligenceResult {
  answer?: string;
  method?: string;
  [key: string]: unknown;
}

/** An error this module wrote: a fixed message for the clinician and the HTTP status, nothing from a body or token. */
export class IntelligenceError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

/** Splits a stream of NDJSON text into parsed events; a line that is not JSON is skipped, never thrown on. */
export function createNdjsonParser(onEvent: (e: IntelligenceEvent) => void) {
  let buffer = '';
  const emitLine = (line: string) => {
    const text = line.trim();
    if (!text) return;
    try {
      const value: unknown = JSON.parse(text);
      if (value && typeof value === 'object' && typeof (value as { type?: unknown }).type === 'string') onEvent(value as IntelligenceEvent);
    } catch {
      // A partial or malformed line carries nothing the panel can trust.
    }
  };
  return {
    push(chunk: string) {
      buffer += chunk;
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) emitLine(line);
    },
    end() {
      emitLine(buffer);
      buffer = '';
    },
  };
}

/** The clinician-facing words for a refused or failed request. The engine's own message is used only when it is JSON. */
async function errorFor(res: Response): Promise<IntelligenceError> {
  let message = '';
  try {
    const body: unknown = await res.json();
    if (body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string') message = (body as { error: string }).error;
  } catch {
    // Not the engine's JSON (an HTML error page from a proxy): fall through to the fixed words.
  }
  if (res.status === 401) return new IntelligenceError('Your session has expired. Sign in again to use Scrips Intelligence.', 401);
  if (res.status === 403) return new IntelligenceError(message || 'Scrips Intelligence is not enabled for this patient.', 403);
  if (res.status === 429) return new IntelligenceError('Scrips Intelligence is busy. Try again in a minute.', 429);
  return new IntelligenceError('Scrips Intelligence is unavailable right now.', res.status);
}

/**
 * Runs one agent and streams its events to `onEvent`. Resolves with every event received; rejects with an
 * IntelligenceError on a refused request. Aborting `signal` ends the request (the engine stops the run on disconnect).
 */
export async function runIntelligence(
  req: IntelligenceRunRequest,
  opts: { onEvent?: (e: IntelligenceEvent) => void; signal?: AbortSignal; fetch?: FetchLike; token?: string | null } = {},
): Promise<IntelligenceEvent[]> {
  const token = opts.token === undefined ? authStorage.getAccessToken() : opts.token;
  if (!token) throw new IntelligenceError('Sign in to use Scrips Intelligence.', 401);
  const f = opts.fetch ?? ((input, init) => fetch(input, init));
  const res = await f(`${INTELLIGENCE_BASE}/run`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json', accept: 'application/x-ndjson' },
    body: JSON.stringify(req),
    signal: opts.signal,
  });
  if (!res.ok) throw await errorFor(res);
  const events: IntelligenceEvent[] = [];
  const parser = createNdjsonParser((e) => {
    events.push(e);
    opts.onEvent?.(e);
  });
  if (!res.body) {
    parser.push(await res.text());
    parser.end();
    return events;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parser.push(decoder.decode(value, { stream: true }));
  }
  parser.push(decoder.decode());
  parser.end();
  return events;
}

export type IntelligenceRunStatus = 'idle' | 'running' | 'done' | 'error';

/** The parts of a finished run the panel shows: the answer, its check verdicts, and why it failed if it did. */
export interface IntelligenceRunView {
  status: IntelligenceRunStatus;
  events: IntelligenceEvent[];
  answer: string | null;
  /** The rule check: true pass, false fail, null not run. */
  rulesPassed: boolean | null;
  /** The fast model check verdict ("pass", "fail", ...), null when not run. */
  modelCheck: string | null;
  error: string | null;
  /** The HTTP status of a refused request (401, 403, 429, 5xx); null when the request was accepted or never sent. */
  failureStatus: number | null;
}

/** Reads a run's events into what the panel shows. Pure, so the panel and its tests read one source. */
export function viewOf(status: IntelligenceRunStatus, events: IntelligenceEvent[], failure: string | null, failureStatus: number | null = null): IntelligenceRunView {
  const last = (type: string) => [...events].reverse().find((e) => e.type === type);
  const done = last('done');
  const engineError = last('error');
  const rule = last('check-rule');
  const fast = last('check-fast');
  const answer = typeof done?.result?.answer === 'string' ? done.result.answer : null;
  return {
    status: engineError && status !== 'running' ? 'error' : status,
    events,
    answer,
    rulesPassed: rule ? rule.pass === true : null,
    modelCheck: fast && typeof fast.verdict === 'string' ? fast.verdict : null,
    error: failure ?? (typeof engineError?.message === 'string' ? engineError.message : null),
    failureStatus: failure ? failureStatus : null,
  };
}

/** One run at a time; a new start cancels the previous run, and unmounting cancels the one in flight. */
export function useIntelligenceRun(deps: { fetch?: FetchLike } = {}) {
  const [status, setStatus] = useState<IntelligenceRunStatus>('idle');
  const [events, setEvents] = useState<IntelligenceEvent[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [failureStatus, setFailureStatus] = useState<number | null>(null);
  const controller = useRef<AbortController | null>(null);
  const fetchImpl = deps.fetch;

  useEffect(() => () => controller.current?.abort(), []);

  const start = useCallback(
    async (req: IntelligenceRunRequest) => {
      controller.current?.abort();
      const ac = new AbortController();
      controller.current = ac;
      setStatus('running');
      setEvents([]);
      setFailure(null);
      setFailureStatus(null);
      try {
        await runIntelligence(req, { signal: ac.signal, fetch: fetchImpl, onEvent: (e) => setEvents((prev) => [...prev, e]) });
        if (!ac.signal.aborted) setStatus('done');
      } catch (e) {
        if (ac.signal.aborted) return;
        setFailure(e instanceof IntelligenceError ? e.message : 'Scrips Intelligence is unavailable right now.');
        setFailureStatus(e instanceof IntelligenceError ? e.status : null);
        setStatus('error');
      }
    },
    [fetchImpl],
  );

  const cancel = useCallback(() => {
    controller.current?.abort();
    controller.current = null;
    setStatus('idle');
  }, []);

  return { ...viewOf(status, events, failure, failureStatus), start, cancel };
}
